import "../styles/globals.css";
import { Montserrat } from "next/font/google";
import type { AppProps } from "next/app";
import English from "../lang/en.json";
import French from "../lang/fr.json";
import Italian from "../lang/it.json";
import German from "../lang/de.json";
import SwissGerman from "../lang/de-ch.json";
import { IntlProvider } from "react-intl";
import { Locale } from "../shared/types";
import { flatten } from "flat";
import { useRouter } from "next/router";
import { localeFromString } from "../shared/i18n/localization";
import { getFrontendTenantConfigurationByName } from "../shared/contexts/tenantsConfigurations";
import { useEffect } from "react";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import { germanTheme } from "@onlinedoctor-ch/docustyle";
import Script from "next/script";

const montserrat = Montserrat({
  weight: ["400", "600"],
  style: ["normal", "italic"],
  subsets: ["latin"],
});

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const {
    locale: localeCandidate,
    defaultLocale,
    query,
    pathname,
    asPath,
  } = router;
  const { tenantSlug } = query;
  const fallbackLocale =
    localeFromString(defaultLocale) ??
    (tenantSlug === "de" ? Locale.de : Locale["de-ch"]);
  const locale = localeFromString(localeCandidate) ?? fallbackLocale;
  const tenantConfiguration = getFrontendTenantConfigurationByName(
    typeof tenantSlug === "string" ? tenantSlug : ""
  );

  useEffect(() => {
    if (
      tenantConfiguration &&
      !tenantConfiguration.enabledPatientLocales.includes(locale)
    ) {
      router.push({ pathname, query }, asPath, { locale: Locale.de });
    }
  }, [tenantConfiguration, locale, router, pathname, query, asPath]);

  let langFile;

  switch (locale) {
    case Locale.de:
      langFile = German;
      break;
    case Locale["de-ch"]:
      langFile = SwissGerman;
      break;
    case Locale.fr:
      langFile = French;
      break;
    case Locale.it:
      langFile = Italian;
      break;
    case Locale.en:
      langFile = English;
      break;

    default:
      tenantSlug === "de" ? (langFile = German) : (langFile = SwissGerman);
      break;
  }

  const theme = extendTheme(tenantConfiguration?.theme ?? germanTheme, {
    fonts: {
      heading: montserrat.style.fontFamily,
      body: montserrat.style.fontFamily,
    },
  });

  return (
    <>
      <Script id="load-dataLayer">
        {`window.dataLayer = window.dataLayer || [];`}
      </Script>
      <IntlProvider locale={locale} messages={flatten(langFile)}>
        <ChakraProvider theme={theme}>
          <Component {...pageProps} />
        </ChakraProvider>
      </IntlProvider>
    </>
  );
}

export default MyApp;
